/**
 * BentoDrawerPreviews — mini anteprime visuali dentro il drawer del
 * BentoGridV2. Un tile può opzionalmente dichiarare `preview: "social" |
 * "stima" | "sito-personale"` e il drawer renderizza la preview reale
 * corrispondente sotto il titolo, prima del body.
 *
 * Regole invalicabili:
 *   • Gli asset social provengono dal composer di produzione
 *     (routes/social/_post_composer.py) — non mockup HTML.
 *   • Il volantino stima è renderizzato con `StaticTemplatePreview`,
 *     ovvero il vero componente A5 usato nel Social Studio (tokens.js
 *     canonici LAYOUTS + COLORS).
 *   • I 4 layout Sito Personale usano gli stessi token font/color della
 *     Vetrina Agente reale (`_VALID_STYLE` in agent_profile.py).
 */
import React from "react";
import StaticTemplatePreview from "@/components/StaticTemplatePreview";

// ─── Property + agency demo condivisi ────────────────────────────
const DEMO_PROPERTY = {
  title: "Trilocale ristrutturato Brera",
  city: "Milano",
  address: "Via Solferino 22",
  price: 690000,
  surface_area: 95,
  rooms: 3,
  bathrooms: 2,
  floor: 4,
};

const DEMO_AGENCY = {
  name: "Nova Casa",
  agent_name: "Marco Bianchi",
  agent_phone: "+39 340 123 4567",
  agent_email: "marco@novacasa.it",
  web: "novacasa.it",
  logo: null,
};

// ─── Chip di personalizzazione (uniforme in tutti e 3 i drawer) ──
// Rende esplicito e visibile QUALI attributi l'agente può personalizzare
// per ciascuna feature. NON è footer piccolo: è un blocco prominente.
function PersonalizationChips({ items, testid }) {
  return (
    <div className="mt-4 rounded-lg bg-[#F1F4ED]/70 border border-[#D4DBC8]/60 px-3 py-2.5" data-testid={testid}>
      <div className="text-[10px] tracking-[0.22em] uppercase text-[#5F6B4E] font-bold mb-1.5">
        Personalizzabile
      </div>
      <div className="flex flex-wrap gap-1.5">
        {items.map((it) => (
          <span
            key={it}
            className="inline-flex items-center rounded-full bg-white border border-[#D4DBC8] px-2 py-[3px] text-[11px] text-[#3F3D38] font-medium leading-tight whitespace-nowrap"
          >
            {it}
          </span>
        ))}
      </div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════
// SOCIAL — POST 1:1 + REEL 9:16 + strip 3 occasioni
// Asset generati dal composer di produzione (`_compose_post_square`).
// ═════════════════════════════════════════════════════════════════
export function SocialDrawerPreview() {
  const posts = {
    post: {
      src: "/landing/social-open-house.v4.jpg",
      label: "Post 1:1 · Open House",
    },
    reel: {
      src: "/landing/social-reel-poster.v4.moderno.jpg",
      label: "Reel 9:16 · Storia",
    },
  };
  const occasioni = [
    { src: "/landing/social-open-house.v4.jpg",    label: "Open House" },
    { src: "/landing/social-venduto.v4.jpg",       label: "Venduto" },
    { src: "/landing/social-prezzo-nuovo.v4.jpg",  label: "Nuovo prezzo" },
  ];
  return (
    <div className="mt-5" data-testid="drawer-preview-social">
      {/* POST + REEL affiancati */}
      <div className="flex gap-3 items-end justify-center">
        <div className="flex flex-col items-center">
          <img
            src={posts.post.src}
            alt={posts.post.label}
            loading="lazy"
            draggable={false}
            className="w-[130px] h-[130px] rounded-lg object-cover border border-[#E5E7E0]"
            style={{ boxShadow: "0 6px 16px -8px rgba(28,28,26,0.18)" }}
            data-testid="drawer-social-post"
          />
          <div className="mt-1.5 text-[10px] tracking-[0.18em] uppercase text-[#6B6A65] font-semibold">
            Post · 1:1
          </div>
        </div>
        <div className="flex flex-col items-center">
          <img
            src={posts.reel.src}
            alt={posts.reel.label}
            loading="lazy"
            draggable={false}
            className="w-[88px] h-[156px] rounded-lg object-cover border border-[#E5E7E0]"
            style={{ boxShadow: "0 6px 16px -8px rgba(28,28,26,0.18)" }}
            data-testid="drawer-social-reel"
          />
          <div className="mt-1.5 text-[10px] tracking-[0.18em] uppercase text-[#6B6A65] font-semibold">
            Reel · 9:16
          </div>
        </div>
      </div>

      {/* Strip occasioni */}
      <div className="mt-4">
        <div className="text-[10px] tracking-[0.22em] uppercase text-[#6B6A65] font-semibold mb-2 text-center">
          Occasioni pronte
        </div>
        <div className="grid grid-cols-3 gap-2">
          {occasioni.map((o) => (
            <div key={o.label} className="flex flex-col items-center">
              <img
                src={o.src}
                alt={o.label}
                loading="lazy"
                draggable={false}
                className="w-full aspect-square rounded-md object-cover border border-[#E5E7E0]"
                data-testid={`drawer-social-occasione-${o.label.toLowerCase().replace(/\s/g, "-")}`}
              />
              <div className="mt-1 text-[10px] text-[#3F3D38] leading-tight">{o.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Chip personalizzazione */}
      <PersonalizationChips
        testid="drawer-social-personalization"
        items={[
          "Logo agenzia",
          "4 layout",
          "15 colori",
          "Prezzo",
          "Testi",
          "Occasione",
        ]}
      />
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════
// STIMA — 3 volantini reali (VolantinoStima con layout+color diversi)
// ═════════════════════════════════════════════════════════════════
export function StimaDrawerPreview() {
  const flyers = [
    { layout: "elegante", color: "verde_salvia",   label: "Elegante" },
    { layout: "moderno",  color: "terracotta_classico", label: "Moderno" },
    { layout: "luxury",   color: "notte",          label: "Luxury" },
  ];
  return (
    <div className="mt-5" data-testid="drawer-preview-stima">
      <div className="grid grid-cols-3 gap-3">
        {flyers.map((f) => (
          <div key={f.label} className="flex flex-col items-center">
            {/* container che scala il volantino A5 */}
            <div className="w-full" style={{ maxWidth: 108 }}>
              <StaticTemplatePreview
                template="volantino_stima"
                layout={f.layout}
                color={f.color}
                property={DEMO_PROPERTY}
                agency={DEMO_AGENCY}
                side="front"
                showWatermark={false}
              />
            </div>
            <div className="mt-1.5 text-[10px] tracking-[0.18em] uppercase text-[#6B6A65] font-semibold">
              {f.label}
            </div>
          </div>
        ))}
      </div>
      <PersonalizationChips
        testid="drawer-stima-personalization"
        items={[
          "Logo agenzia",
          "4 layout",
          "15 colori",
          "Headline",
          "Sottotitolo",
          "CTA",
          "QR",
        ]}
      />
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════
// SITO PERSONALE — 4 mini "browser mockup" con font/palette reali
// dei 4 skin di AgentPublicProfile (elegante/luxury/moderno/minimal)
// ═════════════════════════════════════════════════════════════════
const SITE_STYLES = [
  {
    id: "elegante",
    name: "Elegante",
    bg: "#F5F1EA",              // alabaster (SkinElegante)
    fg: "#1A1817",
    softFg: "#6E665C",
    accent: "#7C8B6E",
    photo: "/landing/apt/cucina-after.jpg",
    heading: { font: "'Bodoni Moda', serif", italic: true, weight: 400, upper: false, size: 22, letter: "-0.02em" },
    tagline: "Immobili di carattere · Milano",
  },
  {
    id: "luxury",
    name: "Lussuoso",
    bg: "#0F1520",              // dark navy (SkinLuxury)
    fg: "#F5EDD8",
    softFg: "#B8A57A",
    accent: "#C8A35A",          // gold
    photo: "/landing/apt/living-after.jpg",
    heading: { font: "'Spectral', serif", italic: true, weight: 200, upper: false, size: 22, letter: "0" },
    tagline: "Real estate boutique",
  },
  {
    id: "moderno",
    name: "Moderno",
    bg: "#FAFAF7",              // chiaro (SkinModerno)
    fg: "#1C1C1A",
    softFg: "#6B6A65",
    accent: "#D65A3B",          // terracotta
    photo: "/landing/apt/bagno-after.jpg",
    heading: { font: "'Archivo', sans-serif", italic: false, weight: 900, upper: true, size: 20, letter: "-0.02em" },
    tagline: "Milano · Brera",
  },
  {
    id: "minimal",
    name: "Minimal",
    bg: "#FFFFFF",              // white (SkinMinimal)
    fg: "#1C1C1A",
    softFg: "#6B6A65",
    accent: "#7C8B6E",
    photo: "/landing/apt/camera-after.jpg",
    heading: { font: "'Space Grotesk', sans-serif", italic: false, weight: 400, upper: false, size: 20, letter: "-0.01em" },
    tagline: "Consulenza immobiliare",
  },
];

function MiniBrowserMockup({ s }) {
  return (
    <div
      className="rounded-lg border border-[#E5E7E0] overflow-hidden flex flex-col"
      style={{ background: s.bg, color: s.fg, aspectRatio: "3 / 4" }}
      data-testid={`drawer-sito-layout-${s.id}`}
    >
      {/* Browser bar */}
      <div className="flex items-center gap-1 px-1.5 py-1 border-b" style={{ background: "#E9E7E2", borderColor: "rgba(0,0,0,0.06)" }}>
        <span className="w-1 h-1 rounded-full" style={{ background: "#F26D6D" }} />
        <span className="w-1 h-1 rounded-full" style={{ background: "#F5C750" }} />
        <span className="w-1 h-1 rounded-full" style={{ background: "#6FCB6D" }} />
        <div className="ml-1 flex-1 rounded-sm px-1 py-[1px] truncate" style={{ background: "#FFFFFF", fontSize: 6, fontFamily: "'Space Grotesk', sans-serif", color: "#6B6A65", letterSpacing: 0.2 }}>
          prontoimmo.it/agente/marco-bianchi
        </div>
      </div>

      {/* Hero · label stile + nome + tagline */}
      <div className="px-2.5 pt-2 pb-1.5 flex-1 flex flex-col justify-between">
        <div>
          <div
            style={{
              fontSize: 7,
              letterSpacing: "0.28em",
              textTransform: "uppercase",
              fontWeight: 700,
              opacity: 0.55,
              fontFamily: "'Space Grotesk', sans-serif",
              color: s.softFg,
            }}
          >
            {s.name}
          </div>
          <div
            className="mt-1 leading-[0.92]"
            style={{
              fontFamily: s.heading.font,
              fontStyle: s.heading.italic ? "italic" : "normal",
              fontWeight: s.heading.weight,
              textTransform: s.heading.upper ? "uppercase" : "none",
              fontSize: s.heading.size,
              letterSpacing: s.heading.letter,
              color: s.fg,
            }}
          >
            Marco<br />Bianchi
          </div>
          <div
            className="mt-1"
            style={{
              fontFamily: s.heading.font,
              fontStyle: s.heading.italic ? "italic" : "normal",
              fontSize: 8,
              color: s.softFg,
              lineHeight: 1.3,
            }}
          >
            {s.tagline}
          </div>
        </div>

        {/* Property strip · 2 mini card */}
        <div className="mt-1.5 flex gap-1">
          <div className="flex-1">
            <img
              src={s.photo}
              alt=""
              loading="lazy"
              draggable={false}
              className="w-full object-cover"
              style={{ aspectRatio: "4/3", borderRadius: 2 }}
            />
            <div style={{ fontSize: 6, marginTop: 1, color: s.softFg, fontFamily: "'Space Grotesk', sans-serif", letterSpacing: "0.05em" }}>Trilocale Brera</div>
            <div style={{ fontSize: 7, fontWeight: 700, color: s.accent, fontFamily: s.heading.font, fontStyle: s.heading.italic ? "italic" : "normal" }}>€ 690.000</div>
          </div>
          <div className="flex-1">
            <div
              className="w-full flex items-center justify-center"
              style={{ aspectRatio: "4/3", background: `${s.accent}22`, borderRadius: 2 }}
            >
              <div style={{ fontSize: 6, color: s.softFg, fontFamily: "'Space Grotesk', sans-serif", letterSpacing: "0.15em", textTransform: "uppercase" }}>+ altri</div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer thin: swatch colore + contatto */}
      <div className="px-2.5 pb-1.5 flex items-center justify-between gap-1">
        <div
          style={{
            fontFamily: "'Space Grotesk', sans-serif",
            fontSize: 6,
            letterSpacing: "0.2em",
            textTransform: "uppercase",
            color: s.softFg,
          }}
        >
          Nova Casa
        </div>
        <div
          className="w-2 h-2 rounded-full shrink-0"
          style={{ background: s.accent, border: `0.5px solid ${s.fg}22` }}
        />
      </div>
    </div>
  );
}

export function SitoPersonaleDrawerPreview() {
  return (
    <div className="mt-5" data-testid="drawer-preview-sito-personale">
      <div className="grid grid-cols-2 gap-2.5">
        {SITE_STYLES.map((s) => (
          <MiniBrowserMockup key={s.id} s={s} />
        ))}
      </div>
      <PersonalizationChips
        testid="drawer-sito-personalization"
        items={[
          "Logo agenzia",
          "4 layout",
          "15 colori",
          "Foto",
          "Bio",
          "Contatti",
          "Annunci",
        ]}
      />
      <div className="mt-2 text-[10.5px] text-[#6B6A65] text-center">
        <span className="underline underline-offset-2">prontoimmo.it/agente/marco-bianchi</span>
      </div>
    </div>
  );
}
